/**
 * Mixpanel for the phone (docs/05 §1, docs/03 §4).
 *
 * Same facade as the TV's `apps/tv/src/analytics` — `init / identify / track / screenView /
 * reset` behind a loader seam, no-op without a token, dynamic `import()` so the SDK never
 * lands in the initial bundle and is only fetched once the page has painted (QA 2026-09-14,
 * MOB-56; see {@link afterFirstPaint}) — with the mobile event set: `login`, `logout`, `screen_view`,
 * and (WP2.2–2.4) `guest_joined`, `queue_add { via:'mobile' }`, `play_now`.
 *
 * **No PII beyond the uid.** `identify(uid)` is the only identifier that leaves the phone;
 * names, e-mails, photos and typed queries are dropped by {@link sanitizeProps}.
 *
 * (Deliberate duplication of the TV module: the two apps ship separate bundles and
 * `packages/shared` is also imported by the API Worker, which must not resolve
 * `mixpanel-browser`. Extracting a shared `@akpl/analytics` package is left to a later WP.)
 */
export type AnalyticsProps = Record<string, string | number | boolean | null | undefined>;

export interface AnalyticsEvent {
  name: string;
  props: AnalyticsProps;
  at: number;
}

export interface Analytics {
  /** True once `init` ran with a non-empty token. */
  readonly enabled: boolean;
  init(token: string, superProps?: AnalyticsProps): void;
  identify(distinctId: string): void;
  track(name: string, props?: AnalyticsProps): void;
  screenView(screen: string): void;
  reset(): void;
  /** Last events (max 50) — dev/test inspection only. */
  buffer(): readonly AnalyticsEvent[];
}

/** The slice of `mixpanel-browser` this module uses (keeps the SDK out of the type graph). */
export interface MixpanelLike {
  init(token: string, config?: Record<string, unknown>): unknown;
  register(props: Record<string, unknown>): void;
  identify(distinctId?: string): unknown;
  track(name: string, props?: Record<string, unknown>): unknown;
  reset(): void;
}

const MAX_BUFFER = 50;

/** Property names that may not leave the phone (docs/04 §5, same list as the TV). */
export const PII_PROP_KEYS: readonly string[] = [
  'q',
  'query',
  'name',
  'displayName',
  'display_name',
  'email',
  'photo',
  'photoUrl',
  'photo_url',
  'title',
  'artist',
  'artists',
  'joinCode',
  'join_code',
  'password',
  'token',
];

/** Drops `undefined` and anything on {@link PII_PROP_KEYS}. */
export function sanitizeProps(props: AnalyticsProps): AnalyticsProps {
  const out: AnalyticsProps = {};
  for (const [key, value] of Object.entries(props)) {
    if (value === undefined) continue;
    if (PII_PROP_KEYS.includes(key)) continue;
    out[key] = value;
  }
  return out;
}

export type MixpanelLoader = () => Promise<MixpanelLike>;

/**
 * The **core** `mixpanel-browser` build (~37 KB gzip): the default entry bundles session
 * recording / autocapture (rrweb, ~130 KB gzip) that this app never uses.
 */
export const loadMixpanel: MixpanelLoader = async () =>
  (await import('mixpanel-browser/dist/mixpanel-core.cjs.js')).default as unknown as MixpanelLike;

/** Schedules the SDK download. Replaced in tests; see {@link afterFirstPaint}. */
export type Schedule = (run: () => void) => void;

/**
 * Runs `run` once the page has painted and the main thread is idle (QA 2026-09-14, MOB-56).
 *
 * Mixpanel is 126 KB of JavaScript that nothing on the first screen needs, and fetching +
 * parsing it while React is still mounting pushes out the first contentful paint on a phone.
 * `init` therefore only *registers* the intent and the events queue in `pending` until the
 * chunk actually lands, which the facade already handles.
 *
 * `requestIdleCallback` gets a 2 s timeout so a permanently busy main thread still loads it;
 * Safari has no `requestIdleCallback`, hence the `setTimeout` fallback.
 */
export const afterFirstPaint: Schedule = (run) => {
  if (typeof window === 'undefined') {
    run();
    return;
  }
  const idle = () => {
    const ric = (
      window as unknown as {
        requestIdleCallback?: (cb: () => void, options?: { timeout: number }) => number;
      }
    ).requestIdleCallback;
    if (typeof ric === 'function') ric(() => run(), { timeout: 2_000 });
    else window.setTimeout(run, 200);
  };
  if (document.readyState === 'complete') idle();
  else window.addEventListener('load', idle, { once: true });
};

export const MIXPANEL_CONFIG = {
  persistence: 'localStorage',
  track_pageview: false,
  autocapture: false,
  record_sessions_percent: 0,
  batch_requests: true,
  remote_settings_mode: 'disabled',
  ip: false,
} as const;

export class MobileAnalytics implements Analytics {
  enabled = false;
  private sdk: MixpanelLike | null = null;
  private loading: Promise<void> | null = null;
  private pending: Array<(mp: MixpanelLike) => void> = [];
  private distinctId: string | null = null;
  private superProps: AnalyticsProps = {};
  private events: AnalyticsEvent[] = [];
  private loader: MixpanelLoader = loadMixpanel;
  private schedule: Schedule = afterFirstPaint;

  /** Test/dev seam: replaces the dynamic `import('mixpanel-browser')`. */
  setLoader(loader: MixpanelLoader): void {
    this.loader = loader;
  }

  /** Test/dev seam: replaces {@link afterFirstPaint} (run the callback synchronously). */
  setSchedule(schedule: Schedule): void {
    this.schedule = schedule;
  }

  init(token: string, superProps: AnalyticsProps = {}): void {
    this.superProps = sanitizeProps(superProps);
    if (!token) {
      // No project configured (local dev, tests): stay a buffer-only no-op.
      this.enabled = false;
      return;
    }
    if (this.enabled) return; // `/config` is fetched once per boot; ignore a re-init.
    this.enabled = true;
    const props = this.superProps;
    // The chunk is fetched **after** the first paint (MOB-56): until it lands every call is
    // buffered in `pending`, so no event is lost and nothing on the boot path waits for it.
    this.loading = new Promise<void>((resolve) => {
      this.schedule(() => {
        void this.loader()
          .then((mp) => {
            mp.init(token, { ...MIXPANEL_CONFIG });
            mp.register({ ...props });
            this.sdk = mp;
            for (const run of this.pending.splice(0)) run(mp);
          })
          .catch(() => {
            // A blocked or offline CDN must never break the app; keep buffering locally.
            this.enabled = false;
            this.pending.length = 0;
          })
          .finally(resolve);
      });
    });
  }

  private send(run: (mp: MixpanelLike) => void): void {
    if (!this.enabled) return;
    if (this.sdk) run(this.sdk);
    else this.pending.push(run);
  }

  identify(distinctId: string): void {
    this.distinctId = distinctId;
    this.send((mp) => mp.identify(distinctId));
  }

  track(name: string, props: AnalyticsProps = {}): void {
    const clean = sanitizeProps(props);
    this.events = [
      ...this.events.slice(-(MAX_BUFFER - 1)),
      {
        name,
        props: { ...this.superProps, ...clean, distinct_id: this.distinctId ?? undefined },
        at: Date.now(),
      },
    ];
    this.send((mp) => mp.track(name, clean));
  }

  screenView(screen: string): void {
    this.track('screen_view', { screen });
  }

  reset(): void {
    this.distinctId = null;
    this.events = [];
    this.send((mp) => mp.reset());
  }

  buffer(): readonly AnalyticsEvent[] {
    return this.events;
  }

  /** Resolves once the SDK chunk finished loading (tests; no-op when disabled). */
  async ready(): Promise<void> {
    await this.loading;
  }
}

export const analytics = new MobileAnalytics();

declare global {
  interface Window {
    __akpAnalytics?: Analytics;
  }
}

if (typeof window !== 'undefined' && import.meta.env.DEV) {
  window.__akpAnalytics = analytics;
}
